<template>
  <div>
    <div class="flex justify-between items-center">
      <div>
        <BotEditorSubHeader
          :title="$t('botEditor.qa.title')"
          :subtitle="$t('botEditor.qa.subtitle')"
        />
      </div>
      <Button id="btn-request-create-qa" severity="primary" @click="openCreateModal">
        <span class="material-icons">add</span>
        {{ $t('botEditor.qa.addButton') }}
      </Button>
    </div>

    <!-- QA Mode Filter Toggle -->
    <div class="mb-6 flex items-center gap-4 bg-slate-800/50 p-4 rounded-lg border border-slate-700">
      <label class="text-sm font-medium text-gray-300">
        {{ $t('botEditor.qa.modeFilterLabel') }}
      </label>
      
      <SelectButton
        v-model="modeFilter"
        :options="modeFilterOptions"
        optionLabel="label"
        optionValue="value"
        :allowEmpty="false"
        @change="onModeFilterChange"
      />
    </div>

    <!-- Lista delle Q&A -->
    <div v-if="loading" class="text-center text-gray-400 p-8">{{ $t('botEditor.qa.loadingText') }}</div>
    <div v-else-if="qas.length === 0" class="text-center text-gray-400 p-8">{{ $t('botEditor.qa.emptyState') }}</div>
    <div v-else id="qa-list" class="flex flex-col gap-3">
      <div
        v-for="qa in qas"
        :key="qa._id"
        data-telemetry-id="qa-list-item"
        class="group bg-gray-800 border border-gray-700 rounded-lg px-5 py-2.5 hover:border-cyan-700/60 hover:shadow-lg hover:shadow-black/20 transition-all cursor-pointer"
        @click="openEditModal(qa)"
      >
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-4 flex-1 min-w-0">
            <!-- Badge per la modalità -->
            <Chip :label="modeClasses[qa.mode].text" :class="['!text-xs !font-semibold !uppercase shrink-0', modeClasses[qa.mode].chipClass]" />
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-white">{{ qa.questions[0] }}</p>
              <p class="text-sm text-gray-400 truncate max-w-md">{{ stripMarkdown(qa.answer) }}</p>
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <Button data-telemetry-id="btn-delete-qa" v-tooltip.top="$t('common.delete')" severity="danger" text @click.stop="openDeleteModal(qa._id)">
              <span class="material-icons">delete</span>
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / edit Q&A. Closing via X / Esc runs closeModal, same as the Cancel button. -->
    <Dialog
      id="qa-modal"
      :visible="showModal"
      modal
      :header="isEditing ? $t('botEditor.qa.editModalTitle') : $t('botEditor.qa.createModalTitle')"
      :draggable="false"
      :style="{ width: '42rem' }"
      :breakpoints="{ '768px': '95vw' }"
      @update:visible="(visible) => { if (!visible) closeModal(); }"
    >
      <!-- The submit Button lives in the Dialog footer (outside this form), so it
           is tied back to the form with form="qa-form". -->
      <form id="qa-form" class="flex flex-col gap-4" @submit.prevent="handleSubmit">
        <div>
          <label for="input-qa-questions" class="block text-gray-400 mb-2">{{ $t('botEditor.qa.questionsLabel') }}</label>
          <Textarea
            id="input-qa-questions"
            v-model="form.questions"
            rows="2"
            autoResize
            class="w-full"
            :placeholder="$t('botEditor.qa.questionsPlaceholder')"
            autofocus
            required
          />
        </div>
        <!-- Esc is deliberately swallowed for the whole answer area (editor text, toolbar and
             its inline link / image popovers): the popovers close themselves on Esc, and the
             Dialog's document-level Esc listener would otherwise close the whole editor and
             drop the unsaved answer. Esc elsewhere in the dialog still closes it. -->
        <div @keydown.esc.stop>
          <label class="block text-gray-400 mb-2">{{ $t('botEditor.qa.answerLabel') }}</label>
          <RichTextEditor v-model="form.answer" />
        </div>
        <div>
          <!-- Select's id lands on its root <div>, so the label is linked via aria-labelledby -->
          <label id="label-qa-mode" class="block text-gray-400 mb-2">{{ $t('botEditor.qa.modeLabel') }}</label>
          <Select
            id="input-qa-mode"
            v-model="form.mode"
            :options="modeOptions"
            optionLabel="label"
            optionValue="value"
            aria-labelledby="label-qa-mode"
            class="w-full"
          />
        </div>
        <Message v-if="formError" severity="error" :closable="false">{{ formError }}</Message>
      </form>

      <template #footer>
        <Button
          id="btn-cancel-qa"
          type="button"
          severity="secondary"
          :label="$t('botEditor.qa.cancelButton')"
          @click="closeModal"
        />
        <Button
          id="btn-save-qa"
          type="submit"
          form="qa-form"
          severity="primary"
          :label="isSubmitting ? $t('botEditor.qa.savingButton') : $t('botEditor.qa.saveButton')"
          :loading="isSubmitting"
          :disabled="isSubmitting"
        />
      </template>
    </Dialog>

    <!-- Delete confirmation: stays open and locked (no Cancel / X / Esc / mask) while
         isDeleting; confirmDelete closes it when the request settles. -->
    <ConfirmActionDialog
      id="delete-qa-modal"
      v-model:visible="showDeleteModal"
      severity="danger"
      icon="warning"
      :title="$t('botEditor.qa.deleteModalTitle')"
      :message="$t('botEditor.qa.deleteModalMessage')"
      :confirm-label="isDeleting ? $t('botEditor.qa.deletingButton') : $t('botEditor.qa.deleteModalConfirm')"
      :cancel-label="$t('botEditor.qa.deleteModalCancel')"
      :loading="isDeleting"
      :confirm-props="{ id: 'btn-confirm-delete-qa' }"
      :cancel-props="{ id: 'btn-cancel-delete-qa' }"
      @confirm="confirmDelete"
      @cancel="closeDeleteModal"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import api from '../../services/api';
import { showToast } from '../../services/toastService';
import BotEditorSubHeader from '../../components/ui/BotEditorSubHeader.vue';
import RichTextEditor from '../../components/ui/RichTextEditor.vue';
import ConfirmActionDialog from '../../components/ui/ConfirmActionDialog.vue';

const { t } = useI18n();

function stripMarkdown(text) {
  return text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(\*|_)(.+?)\1/g, '$2')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/^>\s+/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/^[-*_]{3,}$/gm, '')
    .replace(/\n+/g, ' ')
    .trim()
}
const route = useRoute();
const botId = ref(null);
const workspaceId = computed(() => route.params.workspaceId);

const qas = ref([]);
const loading = ref(true);
const showModal = ref(false);
const isEditing = ref(false);
const isSubmitting = ref(false);
const formError = ref(null);
const modeFilter = ref('all');

// Nuove variabili di stato per il modale di cancellazione
const showDeleteModal = ref(false);
const qaToDeleteId = ref(null);
const isDeleting = ref(false);

// Oggetto per gestire stili e testo dei badge
const modeClasses = computed(() => ({
  contextual: { text: t('botEditor.qa.modeContextual'), chipClass: '!bg-blue-900 !text-blue-300' },
  exact: { text: t('botEditor.qa.modeExact'), chipClass: '!bg-purple-900 !text-purple-300' },
}));

const form = reactive({
  id: null,
  questions: '',
  answer: '',
  mode: 'contextual',
});

// Response-mode choices for the editor's Select (the old <option> list, same values)
const modeOptions = computed(() => [
  { label: t('botEditor.qa.modeContextualOption'), value: 'contextual' },
  { label: t('botEditor.qa.modeExactOption'), value: 'exact' },
]);

const fetchQas = async () => {
  try {
    loading.value = true;
    const params = modeFilter.value !== 'all' ? { mode: modeFilter.value } : {};
    const response = await api.get(`/workspaces/${workspaceId.value}/qa/${botId.value}`, { params });
    qas.value = response.data.data;
  } catch (error) {
    Console.error('Errore nel caricamento delle Q&A:', error);
  } finally {
    loading.value = false;
  }
};

const modeFilterOptions = computed(() => [
  { label: t('botEditor.qa.modeFilterOption.all'), value: 'all' },
  { label: t('botEditor.qa.modeFilterOption.contextual'), value: 'contextual' },
  { label: t('botEditor.qa.modeFilterOption.exact'), value: 'exact' },
]);

const onModeFilterChange = ({ value }) => {
  if (value !== null) {
    localStorage.setItem(`newc-qa-mode-filter-${botId.value}`, value);
    fetchQas();
  }
};

onMounted(() => {
  botId.value = route.params.id;
  const storedMode = localStorage.getItem(`newc-qa-mode-filter-${botId.value}`);
  if (storedMode) {
    modeFilter.value = storedMode;
  }
  fetchQas();
});

const resetForm = () => {
  form.id = null;
  form.questions = '';
  form.answer = '';
  form.mode = 'contextual';
  isEditing.value = false;
  formError.value = null;
};

const openCreateModal = () => {
  resetForm();
  showModal.value = true;
};

const openEditModal = (qa) => {
  resetForm();
  isEditing.value = true;
  form.id = qa._id;
  form.questions = qa.questions.join('\n');
  form.answer = qa.answer;
  form.mode = qa.mode;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  formError.value = null;

  if (!form.answer.trim()) {
    isSubmitting.value = false;
    return;
  }

  const payload = {
    questions: form.questions.split('\n').filter(q => q.trim() !== ''),
    answer: form.answer,
    mode: form.mode,
  };

  try {
    if (isEditing.value) {
      await api.put(`/workspaces/${workspaceId.value}/qa/${form.id}`, payload);
    } else {
      await api.post(`/workspaces/${workspaceId.value}/qa/${botId.value}`, payload);
    }
    closeModal();
    await fetchQas();
  } catch (error) {
    Console.error('Errore nel salvataggio della Q&A:', error);
    formError.value = error.response?.data?.error?.message || t('common.error');
  } finally {
    isSubmitting.value = false;
  }
};

// Nuove funzioni per il flusso di cancellazione
const openDeleteModal = (id) => {
  qaToDeleteId.value = id;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  qaToDeleteId.value = null;
  showDeleteModal.value = false;
};

const confirmDelete = async () => {
  // Capture the id first: closeDeleteModal() nulls qaToDeleteId
  const id = qaToDeleteId.value;
  if (!id || isDeleting.value) return;
  isDeleting.value = true;
  try {
    await api.delete(`/workspaces/${workspaceId.value}/qa/${id}`);
    qas.value = qas.value.filter(q => q._id !== id);
  } catch (error) {
    Console.error('Errore nella cancellazione della Q&A:', error);
    showToast(t('botEditor.qa.deleteErrorMessage'), 'error');
    // Re-sync with the server: the delete may still have completed after a client-side timeout
    await fetchQas();
  } finally {
    isDeleting.value = false;
    closeDeleteModal();
  }
};
</script>